// src/components/Dashboard.js - With Toast notifications and Publish Confirm Modal
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  onAuthStateChanged,
  signOut,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc, updateDoc, setDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import styles from "./Dashboard.module.css";
import DashboardHeader from "./DashboardHeader";
import DashboardNavigation from "./DashboardNavigation";
import DashboardContent from "./DashboardContent";
import BottomMobileNav from "./BottomMobileNav";
import QRCodeModal from "./QRCodeModal";
import FullScreenPreview from "./FullScreenPreview";
import HelpSystem from "./HelpSystem"; // Import the Help System
import PublishConfirmModal from "./PublishConfirmModal"; // Import the Publish Confirm Modal
import { useToast } from "../context/ToastContext"; // Import Toast Hook

// Import the Pro Feature Provider
import { ProFeatureProvider } from "../context/ProFeatureContext";

// Palette utilities
import { applyPalette, resetPalette } from "../utils/paletteUtils";
// Theming
import { applyTheme } from "../themes";

// Stripe payment link for upgrading - fallback if cloud function fails
const STRIPE_PAYMENT_LINK = "https://buy.stripe.com/dR614x7iH0E44366oo";

// Session expiry set to 7 days in milliseconds
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);

  // Whether the side menu (drawer) is open
  const [menuOpen, setMenuOpen] = useState(false);

  // Which dashboard section is active: "preview", "editProfile", etc.
  // Initialize with empty string to show the welcome page by default
  const [activeSection, setActiveSection] = useState("");

  // For showing/hiding the QR Code Modal
  const [isQrCodeModalOpen, setIsQrCodeModalOpen] = useState(false);

  // For showing/hiding the publish confirm modal
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);

  // For showing/hiding the full-screen preview
  const [isFullScreenPreviewOpen, setIsFullScreenPreviewOpen] = useState(false);
  
  // Reference to the help system component
  const helpSystemRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError, showInfo } = useToast(); // Get toast functions

  // Keep track of the last-saved profile to detect changes for autosaving
  const lastSavedProfile = useRef(null);

  // Evaluate if user is Pro tier with improved logic to check subscription end date
  const isProUser = userData?.tierType === "pro" || 
    (userData?.proEndDate && new Date(userData.proEndDate) > new Date());

  // Check for activeSection in location state
  useEffect(() => {
    if (location.state && location.state.activeSection !== undefined) {
      setActiveSection(location.state.activeSection);
    }
  }, [location.state]);

  // On mount, set persistence and listen for auth changes
  useEffect(() => {
    console.log("Dashboard mounting - setting up auth listener");
    // If you want to start with the menu open, set menuOpen to true:
    setMenuOpen(true);

    // Force local persistence so that user stays signed in across reloads
    setPersistence(auth, browserLocalPersistence)
      .then(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
          const expiry = localStorage.getItem("sessionExpiry");

          // Check if session has expired
          if (expiry && Date.now() > Number(expiry)) {
            console.log("Session expired. Signing out...");
            await signOut(auth);
            resetPalette();
            localStorage.removeItem("sessionExpiry");
            navigate("/");
            return;
          }

          if (firebaseUser) {
            console.log("User authenticated:", firebaseUser.uid);
            setUser(firebaseUser);

            // Extend session by 7 days from now
            localStorage.setItem("sessionExpiry", Date.now() + SEVEN_DAYS_MS);

            // Fetch Firestore user doc
            try {
              const userDocRef = doc(db, "users", firebaseUser.uid);
              const userDoc = await getDoc(userDocRef);

              if (userDoc.exists()) {
                // Incorporate the Firebase UID
                const dataWithUid = { ...userDoc.data(), uid: firebaseUser.uid };
                console.log("User data loaded successfully:", dataWithUid.username);
                
                // Check if this is a new user or user without a banner
                if (!dataWithUid.profile?.banner) {
                  console.log("New user detected or user missing banner, initializing default banner");
                  const defaultBanner = {
                    url: "/images/banner.jpg",
                    position: 50,
                    isDefault: true
                  };
                  
                  // Update the user data with default banner
                  try {
                    // Ensure profile object exists before updating
                    if (!dataWithUid.profile) {
                      await updateDoc(userDocRef, {
                        profile: {
                          banner: defaultBanner
                        }
                      });
                      dataWithUid.profile = { banner: defaultBanner };
                    } else {
                      await updateDoc(userDocRef, {
                        "profile.banner": defaultBanner
                      });
                      dataWithUid.profile.banner = defaultBanner;
                    }
                    
                    console.log("Default banner set successfully");
                  } catch (error) {
                    console.error("Error setting default banner:", error);
                  }
                }
                
                // Check if proEndDate exists and is in the past
                if (dataWithUid.proEndDate) {
                  const proEndDate = new Date(dataWithUid.proEndDate);
                  const now = new Date();
                  
                  if (proEndDate < now) {
                    console.log("Pro subscription has ended, downgrading user");
                    
                    // Downgrade the user if their pro period has ended
                    try {
                      await updateDoc(userDocRef, {
                        tierType: "free"
                      });
                      
                      // Update the local data to reflect the change
                      dataWithUid.tierType = "free";
                      
                      console.log("User downgraded to free tier");
                    } catch (error) {
                      console.error("Error downgrading user:", error);
                    }
                  } else {
                    console.log("User has Pro until:", proEndDate.toISOString());
                    // Ensure tierType is "pro" if they are still in their paid period
                    if (dataWithUid.tierType !== "pro" && 
                        dataWithUid.subscriptionStatus === "canceled") {
                      console.log("User has canceled but still in paid period, keeping Pro access");
                      dataWithUid.tierType = "pro"; // Ensure they still have Pro access locally
                    }
                  }
                }
                
                setUserData(dataWithUid);

                // Apply palette, if any
                if (dataWithUid.profile?.palette) {
                  applyPalette(dataWithUid.profile.palette);
                }
                // Apply theme, if any
                if (dataWithUid.profile?.theme) {
                  applyTheme(dataWithUid.profile.theme);
                }

                // Record the current profile as the last saved version
                lastSavedProfile.current = dataWithUid.profile;
              } else {
                console.log("No user document found for:", firebaseUser.uid);
              }
            } catch (error) {
              console.error("Error fetching user data:", error);
            }
          } else {
            console.log("No authenticated user");
            resetPalette();
            navigate("/");
          }
        });
        return () => {
          console.log("Unsubscribing from auth state changes");
          unsubscribe();
        };
      })
      .catch((error) => {
        console.error("Error setting persistence:", error);
      });
  }, [navigate, showError]);

  // Create or update a username mapping doc whenever userData loads
  useEffect(() => {
    async function createUsernameMapping() {
      if (!userData?.username || !user?.uid) {
        console.log("Cannot create username mapping: missing username or user ID");
        return;
      }

      try {
        console.log(`Creating username mapping for ${userData.username} -> ${user.uid}`);

        // Write or overwrite 'usernames/{username}' doc
        await setDoc(doc(db, "usernames", userData.username), {
          userId: user.uid,
          createdAt: new Date(),
        });

        console.log("Username mapping created successfully!");
      } catch (error) {
        console.error("Error creating username mapping:", error);
      }
    }

    createUsernameMapping();
  }, [userData?.username, user?.uid]);

  // Open the publish confirmation modal
  const openPublishModal = () => {
    setIsPublishModalOpen(true);
  };

  // Publish changes: copy user doc into "commit" collection for public usage
  const handlePublish = async () => {
    try {
      // Show a "Publishing..." toast
      showInfo("Publishing your profile...");
      
      if (!user || !userData) {
        throw new Error("User data not available");
      }

      // Get current user doc
      const userDocRef = doc(db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        throw new Error("User data not found");
      }

      const currentUserData = userDocSnap.data();
      const currentProfile = currentUserData.profile || {};
      const lastPublished = new Date().toISOString();
      const publicBanner = currentProfile.banner?.url
        ? {
            url: currentProfile.banner.url,
            position: currentProfile.banner.position ?? 50,
          }
        : null;
      const publicLinks = Array.isArray(currentProfile.links)
        ? currentProfile.links.map((link) => ({
            id: link.id || "",
            title: link.title || "",
            url: link.url || "",
            hidden: link.hidden || false,
            pinned: link.pinned || false,
            order: link.order || 0,
            imageUrl: link.imageUrl || null,
            ghostProtected: link.ghostProtected || false,
          }))
        : [];
      const publicIcons = Array.isArray(currentProfile.icons)
        ? currentProfile.icons.map((icon) => ({
            name: icon.name || "",
            component: icon.component || "",
            url: icon.url || "",
            color: icon.color || null,
          }))
        : [];

      const updatedData = {
        uid: user.uid,
        username: currentUserData.username || "",
        displayUsername: currentUserData.displayUsername || currentUserData.username,
        photoURL: currentUserData.photoURL || "",
        profile: {
          displayName: currentProfile.displayName || "",
          bio: currentProfile.bio || "",
          email: currentProfile.email || "",
          phone: currentProfile.phone || "",
          textColor: currentProfile.textColor || "#444444",
          textOpacity: currentProfile.textOpacity ?? 70,
          icons: publicIcons,
          links: publicLinks,
          palette: currentProfile.palette || null,
          banner: publicBanner,
          theme: currentProfile.theme || "classic",
          template: currentProfile.template || null,
          public: currentProfile.public !== false,
          lastPublished,
          ghostMode: {
            enabled: currentProfile.ghostMode?.enabled === true,
            customMessage: currentProfile.ghostMode?.customMessage || "",
          },
        },
      };

      // Write to "commit/{uid}" doc
      const commitDocRef = doc(db, "commit", user.uid);
      await setDoc(commitDocRef, updatedData);

      console.log("Published to commit collection:", updatedData);

      // Also update the "lastPublished" field in users collection
      await updateDoc(userDocRef, {
        "profile.lastPublished": lastPublished,
      });

      // Update local state
      setUserData((prev) => ({
        ...prev,
        profile: {
          ...prev.profile,
          lastPublished,
        },
      }));

      // Show success toast
      showSuccess("Profile published successfully! Your changes are now live.");
      return true;
    } catch (error) {
      console.error("Error publishing changes:", error);
      // Show error toast
      showError(`Publishing failed: ${error.message || 'Unknown error'}`);
      throw error;
    }
  };

  // Copy the user's public profile link to clipboard
  const handleCopyProfile = () => {
    if (!userData) return;
    const profileUrl = `${window.location.origin}/${userData.username}`;
    
    return navigator.clipboard
      .writeText(profileUrl)
      .then(() => {
        // Show success toast
        showSuccess("Profile link copied to clipboard!");
        return true;
      })
      .catch((error) => {
        console.error("Error copying to clipboard:", error);
        // Show error toast
        showError("Could not copy profile link. Please try again.");
        throw error;
      });
  };

  // QR Code Modal toggling
  const openQrCodeModal = () => {
    showInfo("Opening QR code...");
    setIsQrCodeModalOpen(true);
  };
  const closeQrCodeModal = () => setIsQrCodeModalOpen(false);

  // Full screen preview toggling
  const openFullScreenPreview = () => {
    showInfo("Opening preview...");
    setIsFullScreenPreviewOpen(true);
  };
  const closeFullScreenPreview = () => setIsFullScreenPreviewOpen(false);

  // Help system opening function
  const openHelp = () => {
    if (helpSystemRef.current) {
      helpSystemRef.current.openHelp();
    }
  };

  // Stripe Payment redirect using Cloud Function
  const handleUpgrade = async () => {
    console.log("Upgrade button clicked");
    
    showInfo("Preparing payment options...");
    
    if (user && user.uid) {
      // Store the user ID as a fallback mechanism
      localStorage.setItem("upgradeUserId", user.uid);
      
      try {
        console.log("Calling createCheckoutSession function");
        // Get Firebase Functions instance
        const functions = getFunctions();
        
        // Create a reference to the createCheckoutSession function
        const createCheckoutSession = httpsCallable(functions, "createCheckoutSession");
        
        // Call the function (no parameters needed as we get user ID from auth)
        const result = await createCheckoutSession();
        console.log("Function result:", result);
        
        // If we got back a URL, redirect to Stripe checkout
        if (result.data && result.data.url) {
          console.log("Redirecting to:", result.data.url);
          window.location.href = result.data.url;
        } else {
          console.error("No checkout URL returned");
          // Fall back to direct payment link with session_id parameter
          window.location.href = STRIPE_PAYMENT_LINK + "?redirect_to=" + 
            encodeURIComponent(window.location.origin + "/payment-success");
        }
      } catch (error) {
        console.error("Error creating checkout session:", error);
        showError("Could not create checkout session. Redirecting to backup payment page.");
        // Fall back to direct payment link on error
        window.location.href = STRIPE_PAYMENT_LINK + "?redirect_to=" + 
          encodeURIComponent(window.location.origin + "/payment-success");
      }
    } else {
      // If no user is logged in (shouldn't happen in protected route)
      window.location.href = STRIPE_PAYMENT_LINK + "?redirect_to=" + 
        encodeURIComponent(window.location.origin + "/payment-success");
    }
  };

  // If a Pro feature is clicked while not Pro, do something
  const handleProFeatureClick = () => {
    // The notification will be handled by the ProFeatureContext
    // No additional code needed here as the components will call the context directly
  };

  // Sign out the current user
  const handleSignOut = async () => {
    try {
      resetPalette();
      console.log("Signing out user");
      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
      showError("Error signing out. Please try again.");
    }
  };

  /**
   * Autosave logic:
   *  - Compares the user's current profile in state to the last saved profile.
   *  - If changes exist, it waits 2s, then does a partial update to Firestore.
   */
  useEffect(() => {
    if (!user || !userData?.profile) return;

    // Current profile in state
    const currentProfile = userData.profile;
    // Last saved version in ref
    const savedProfile = lastSavedProfile.current;

    // Check if banner has disableAutoSave flag - if so, skip autosaving banner
    const shouldSkipBannerAutoSave = 
      currentProfile.banner && 
      (currentProfile.banner.disableAutoSave || currentProfile.banner.preventEditingStateChange);

    // Compare. If no changes, skip saving
    const hasChanges =
      !savedProfile ||
      currentProfile.displayName !== savedProfile.displayName ||
      currentProfile.email !== savedProfile.email ||
      currentProfile.phone !== savedProfile.phone ||
      currentProfile.bio !== savedProfile.bio ||
      currentProfile.textColor !== savedProfile.textColor ||
      currentProfile.textOpacity !== savedProfile.textOpacity ||
      JSON.stringify(currentProfile.icons) !== JSON.stringify(savedProfile.icons) ||
      JSON.stringify(currentProfile.links) !== JSON.stringify(savedProfile.links) ||
      JSON.stringify(currentProfile.palette) !== JSON.stringify(savedProfile.palette) ||
      (!shouldSkipBannerAutoSave && JSON.stringify(currentProfile.banner) !== JSON.stringify(savedProfile.banner)) ||
      currentProfile.theme !== savedProfile.theme;

    if (!hasChanges) {
      console.log("Autosave skipped - no changes detected");
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        console.log("Autosave triggered. Current profile:", currentProfile);
        const userDocRef = doc(db, "users", user.uid);

        // Build a partial update object
        const updates = {};
        if (currentProfile.displayName !== undefined) {
          updates["profile.displayName"] = currentProfile.displayName;
        }
        if (currentProfile.email !== undefined) {
          updates["profile.email"] = currentProfile.email;
        }
        if (currentProfile.phone !== undefined) {
          updates["profile.phone"] = currentProfile.phone;
        }
        if (currentProfile.bio !== undefined) {
          updates["profile.bio"] = currentProfile.bio;
        }
        if (currentProfile.textColor !== undefined) {
          updates["profile.textColor"] = currentProfile.textColor;
        }
        if (currentProfile.textOpacity !== undefined) {
          updates["profile.textOpacity"] = currentProfile.textOpacity;
        }
        if (currentProfile.icons !== undefined) {
          updates["profile.icons"] = currentProfile.icons;
        }
        if (currentProfile.links !== undefined) {
          updates["profile.links"] = currentProfile.links;
        }
        if (currentProfile.palette !== undefined) {
          updates["profile.palette"] = currentProfile.palette;
        }
        
        // Only include banner in updates if it doesn't have the disableAutoSave flag
        if (currentProfile.banner !== undefined && !shouldSkipBannerAutoSave) {
          // Clean the banner data by removing temporary flags
          if (currentProfile.banner) {
            const { preventEditingStateChange, disableAutoSave, ...cleanBannerData } = currentProfile.banner;
            updates["profile.banner"] = cleanBannerData;
          } else {
            updates["profile.banner"] = currentProfile.banner;
          }
        }
        
        if (currentProfile.theme !== undefined) {
          updates["profile.theme"] = currentProfile.theme;
        }

        if (Object.keys(updates).length > 0) {
          await updateDoc(userDocRef, updates);
          console.log("Autosaved profile (partial update).", updates);
          
          // Create a clean copy for lastSavedProfile reference
          const cleanedProfile = { ...currentProfile };
          
          // Clean any banner flags before storing in lastSavedProfile
          if (cleanedProfile.banner) {
            const { preventEditingStateChange, disableAutoSave, ...cleanBannerData } = cleanedProfile.banner;
            cleanedProfile.banner = cleanBannerData;
          }
          
          // Deep copy into ref so we don't get stale references
          lastSavedProfile.current = JSON.parse(JSON.stringify(cleanedProfile));
        }
      } catch (error) {
        console.error("Error autosaving profile:", error);
        // Don't show toast for autosave errors to avoid bothering the user
      }
    }, 2000);

    // Clear timeout if component unmounts or userData changes again
    return () => clearTimeout(timeoutId);
  }, [user, userData?.profile]);

  // If we haven't loaded user or userData yet, show a simple fallback
  if (!user || !userData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        Loading...
      </div>
    );
  }

  // The profile URL for QR code
  const profileUrl = `${window.location.origin}/${userData.username}`;

  return (
    <ProFeatureProvider onUpgradeClick={handleUpgrade}>
      <div className={styles.dashboardContainer}>
        {/* Top Header */}
        <DashboardHeader
          menuOpen={menuOpen}
          setMenuOpen={setMenuOpen}
          setActiveSection={setActiveSection}
          handlePublish={openPublishModal} // Change to open the modal instead
          handleCopyProfile={handleCopyProfile}
          openQrCodeModal={openQrCodeModal}
          openFullScreenPreview={openFullScreenPreview}
          isProUser={isProUser}
        />

        {/* Side Navigation */}
        <DashboardNavigation
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          userData={userData}
          handleUpgrade={handleUpgrade}
          handleProFeatureClick={handleProFeatureClick}
          handleSignOut={handleSignOut}
          menuOpen={menuOpen}
          isProUser={isProUser}
          openHelp={openHelp} // Pass the help opening function
        />

        {/* Main Content Section */}
        <div className={styles.contentWrapper}>
          <DashboardContent
            activeSection={activeSection}
            user={user}
            userData={userData}
            setUserData={setUserData}
            setActiveSection={setActiveSection}
            isProUser={isProUser}
            onUpgradeClick={handleUpgrade}
          />
        </div>

        {/* Bottom Mobile Navigation */}
        <BottomMobileNav
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          isProUser={isProUser}
          handleProFeatureClick={handleProFeatureClick}
          handleSignOut={handleSignOut}
          openHelp={openHelp} // Pass the help opening function for mobile
        />

        {/* QR Code Modal */}
        <QRCodeModal
          isOpen={isQrCodeModalOpen}
          onClose={closeQrCodeModal}
          profileUrl={profileUrl}
          username={userData.username}
        />

        {/* Publish Confirmation Modal */}
        <PublishConfirmModal
          isOpen={isPublishModalOpen}
          onClose={() => setIsPublishModalOpen(false)}
          onConfirm={() => {
            setIsPublishModalOpen(false);
            handlePublish();
          }}
        />

        {/* Full Screen Preview */}
        <FullScreenPreview
          isOpen={isFullScreenPreviewOpen}
          onClose={closeFullScreenPreview}
          userId={user.uid}
          onPublish={openPublishModal} // Change to open the modal instead
        />
        
        {/* Help System */}
        <HelpSystem
          ref={helpSystemRef}
          userId={user.uid}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
        />
      </div>
    </ProFeatureProvider>
  );
};

export default Dashboard;
